/**
 * Role Permissions Management Tab
 * 
 * Allows owners to manage role-based access control for all admin panel sections.
 * Select a role, view checkboxes for each section/subtab, toggle access on/off.
 * Includes role templates for quick setup and permission inheritance display.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Check, X, RefreshCw, Save, RotateCcw, ChevronDown,
  ChevronRight, Lock, Users, Eye, Edit, AlertTriangle, Info,
  Layers, Wallet, ChefHat, Table, Megaphone, Package, Clock, Calculator, Tablet,
  GitBranch, Copy, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Template icon mapping
const TEMPLATE_ICONS = {
  'cash-drawer': Wallet,
  'chef-hat': ChefHat,
  'table': Table,
  'megaphone': Megaphone,
  'package': Package,
  'clock': Clock,
  'calculator': Calculator,
  'tablet': Tablet,
};

// Role badge colors
const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  supervisor: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  accountant: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cashier: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  kitchen: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  staff: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

export default function RolePermissionsTab({ authHeaders }) {
  const [allData, setAllData] = useState(null);
  const [selectedRole, setSelectedRole] = useState('mgr-sup');
  const [permissions, setPermissions] = useState({ sections: [], subtabs: {} });
  const [originalPermissions, setOriginalPermissions] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const [activeTab, setActiveTab] = useState('permissions');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  
  // Inheritance state
  const [inheritanceTree, setInheritanceTree] = useState(null);
  
  // Fetch all permissions data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/permissions/all');
      setAllData(res.data);
      
      // Load selected role's permissions
      if (res.data.permissions[selectedRole]) {
        const rolePerms = res.data.permissions[selectedRole];
        setPermissions(rolePerms.permissions);
        setOriginalPermissions(JSON.stringify(rolePerms.permissions));
        setIsCustom(rolePerms.is_custom);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);
  
  // Load role permissions when role changes
  const loadRolePermissions = useCallback(async (role) => {
    if (!allData) return;
    
    const rolePerms = allData.permissions[role];
    if (rolePerms) {
      setPermissions(rolePerms.permissions);
      setOriginalPermissions(JSON.stringify(rolePerms.permissions));
      setIsCustom(rolePerms.is_custom);
      setHasChanges(false);
    }
  }, [allData]);
  
  // Initial load
  useEffect(() => {
    fetchAllData();
    fetchTemplates();
    fetchInheritanceTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAllData]);
  
  // Fetch templates
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await api.get('/admin/permissions/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  // Fetch inheritance tree
  const fetchInheritanceTree = async () => {
    try {
      const res = await api.get('/admin/permissions/inheritance-tree');
      setInheritanceTree(res.data);
    } catch (err) {
      console.error('Failed to fetch inheritance tree:', err);
    }
  };
  
  // Apply template to role
  const handleApplyTemplate = async () => {
    if (!selectedTemplate || selectedRole === 'owner') return;
    
    setApplyingTemplate(true);
    try {
      const res = await api.post(`/admin/permissions/role/${selectedRole}/apply-template/${selectedTemplate.id}`);
      
      toast.success(`Template "${selectedTemplate.name}" applied to ${selectedRole}`);
      setPermissions(res.data.permissions);
      setOriginalPermissions(JSON.stringify(res.data.permissions));
      setIsCustom(true);
      setHasChanges(false);
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      
      // Refresh all data
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };
  
  // Load permissions when role changes
  useEffect(() => {
    if (allData) {
      loadRolePermissions(selectedRole);
    }
  }, [selectedRole, allData, loadRolePermissions]);
  
  // Check for changes
  useEffect(() => {
    if (originalPermissions) {
      setHasChanges(JSON.stringify(permissions) !== originalPermissions);
    }
  }, [permissions, originalPermissions]);
  
  // Toggle section access
  const toggleSection = (sectionId) => {
    const newSections = permissions.sections.includes(sectionId)
      ? permissions.sections.filter(s => s !== sectionId)
      : [...permissions.sections, sectionId];
    
    // If removing section, also remove all its subtabs
    const newSubtabs = { ...permissions.subtabs };
    if (!newSections.includes(sectionId)) {
      delete newSubtabs[sectionId];
    }
    
    setPermissions({ sections: newSections, subtabs: newSubtabs });
  };
  
  // Toggle subtab access
  const toggleSubtab = (sectionId, subtabId) => {
    const currentSubtabs = permissions.subtabs[sectionId] || [];
    const newSubtabs = currentSubtabs.includes(subtabId)
      ? currentSubtabs.filter(s => s !== subtabId)
      : [...currentSubtabs, subtabId];
    
    setPermissions({
      ...permissions,
      subtabs: {
        ...permissions.subtabs,
        [sectionId]: newSubtabs
      }
    });
    
    // Ensure section is enabled if any subtab is enabled
    if (newSubtabs.length > 0 && !permissions.sections.includes(sectionId)) {
      setPermissions(prev => ({
        ...prev,
        sections: [...prev.sections, sectionId],
        subtabs: {
          ...prev.subtabs,
          [sectionId]: newSubtabs
        }
      }));
    }
  };
  
  // Select/deselect all subtabs in a section
  const toggleAllSubtabs = (sectionId, selectAll) => {
    const section = allData?.sections?.[sectionId];
    if (!section) return;
    
    const allSubtabIds = section.subtabs.map(s => s.id);
    
    if (selectAll) {
      // Select all subtabs and enable section
      setPermissions(prev => ({
        sections: prev.sections.includes(sectionId) ? prev.sections : [...prev.sections, sectionId],
        subtabs: {
          ...prev.subtabs,
          [sectionId]: allSubtabIds
        }
      }));
    } else {
      // Deselect all subtabs
      setPermissions(prev => ({
        ...prev,
        subtabs: {
          ...prev.subtabs,
          [sectionId]: []
        }
      }));
    }
  };
  
  // Save permissions
  const handleSave = async () => {
    if (selectedRole === 'owner') {
      toast.error('Cannot modify owner permissions');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/admin/permissions/role/${selectedRole}`, {
        role: selectedRole,
        sections: permissions.sections,
        subtabs: permissions.subtabs
      });
      
      toast.success(`Permissions saved for ${selectedRole}`);
      setOriginalPermissions(JSON.stringify(permissions));
      setIsCustom(true);
      setHasChanges(false);
      
      // Refresh all data
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const handleReset = async () => {
    if (selectedRole === 'owner') {
      toast.error('Cannot modify owner permissions');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/admin/permissions/role/${selectedRole}/reset`);
      
      toast.success(`Permissions reset to defaults for ${selectedRole}`);
      setPermissions(res.data.permissions);
      setOriginalPermissions(JSON.stringify(res.data.permissions));
      setIsCustom(false);
      setHasChanges(false);
      setShowResetDialog(false);
      
      // Refresh all data
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };
  
  // Discard changes
  const handleDiscard = () => {
    if (originalPermissions) {
      setPermissions(JSON.parse(originalPermissions));
      setHasChanges(false);
    }
  };
  
  // Get count of enabled subtabs for a section
  const getEnabledCount = (sectionId) => {
    const section = allData?.sections?.[sectionId];
    if (!section) return { enabled: 0, total: 0 };
    
    const enabled = (permissions.subtabs[sectionId] || []).length;
    const total = section.subtabs.length;
    return { enabled, total };
  };
  
  // Check if section has any enabled subtabs
  const isSectionEnabled = (sectionId) => {
    return permissions.sections.includes(sectionId);
  };
  
  if (loading || !allData) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const currentRoleInfo = allData.roles.find(r => r.id === selectedRole);
  
  return (
    <div className="space-y-6" data-testid="role-permissions-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Role Permissions</h2>
          <p className="text-muted-foreground">
            Manage what each role can access in the admin panel
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Role Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Select Role to Configure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-full sm:w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allData.roles.map(role => (
                  <SelectItem key={role.id} value={role.id}>
                    <div className="flex items-center gap-2">
                      {role.id === 'owner' && <Lock className="w-3 h-3" />}
                      {role.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1">
              {currentRoleInfo && (
                <div className="flex items-center gap-3">
                  <Badge className={ROLE_COLORS[selectedRole]}>
                    {currentRoleInfo.label}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {currentRoleInfo.description}
                  </span>
                  {isCustom && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      Custom
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Owner Warning */}
      {selectedRole === 'owner' && (
        <Alert>
          <Lock className="w-4 h-4" />
          <AlertDescription>
            Owner role has full access to all sections and cannot be modified.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Tabs for Permissions, Templates, Inheritance */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Permissions</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="inheritance" className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            <span className="hidden sm:inline">Inheritance</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Quick Setup Templates
              </CardTitle>
              <CardDescription>
                Apply pre-built permission sets to quickly configure roles. Click a template to preview and apply it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No templates available</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(template => {
                    const IconComponent = TEMPLATE_ICONS[template.icon] || Layers;
                    return (
                      <motion.div
                        key={template.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                          selectedRole === 'owner'
                            ? 'opacity-50 cursor-not-allowed border-border bg-muted/30'
                            : 'border-border hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10'
                        }`}
                        onClick={() => {
                          if (selectedRole !== 'owner') {
                            setSelectedTemplate(template);
                            setShowTemplateDialog(true);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-foreground truncate">{template.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {template.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {template.section_count} sections
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {template.subtab_count} tabs
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
              
              {selectedRole === 'owner' && (
                <Alert className="mt-4">
                  <Lock className="w-4 h-4" />
                  <AlertDescription>
                    Select a different role to apply templates. Owner permissions cannot be modified.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Inheritance Tab */}
        <TabsContent value="inheritance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Permission Inheritance
              </CardTitle>
              <CardDescription>
                Roles can inherit permissions from other roles. This helps maintain consistent access hierarchies.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {inheritanceTree ? (
                <div className="space-y-4">
                  {/* Inheritance Diagram */}
                  <div className="p-4 bg-muted/30 dark:bg-muted/10 rounded-xl">
                    <h4 className="font-medium text-foreground mb-3">Hierarchy</h4>
                    <div className="space-y-2">
                      {Object.entries(inheritanceTree.inheritance_tree).map(([roleId, info]) => (
                        <div 
                          key={roleId}
                          className={`flex items-center gap-2 p-2 rounded-lg ${
                            roleId === selectedRole 
                              ? 'bg-primary/10 dark:bg-primary/20 border border-primary/30' 
                              : ''
                          }`}
                        >
                          <Badge className={ROLE_COLORS[roleId] || 'bg-gray-100 text-gray-700'}>
                            {info.label}
                          </Badge>
                          {info.inherits_from && (
                            <>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                inherits from{' '}
                                <span className="font-medium text-foreground">
                                  {inheritanceTree.inheritance_tree[info.inherits_from]?.label || info.inherits_from}
                                </span>
                              </span>
                            </>
                          )}
                          {info.inherited_by && info.inherited_by.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-auto">
                              ({info.inherited_by.length} inherit)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Current Role Inheritance Info */}
                  {selectedRole && inheritanceTree.inheritance_tree[selectedRole] && (
                    <div className="p-4 border border-border rounded-xl">
                      <h4 className="font-medium text-foreground mb-2">
                        {inheritanceTree.inheritance_tree[selectedRole].label} Role
                      </h4>
                      <div className="space-y-2 text-sm">
                        {inheritanceTree.inheritance_tree[selectedRole].inherits_from ? (
                          <p className="text-muted-foreground">
                            <span className="text-primary font-medium">Inherits from:</span>{' '}
                            {inheritanceTree.inheritance_tree[inheritanceTree.inheritance_tree[selectedRole].inherits_from]?.label}
                            {' '}(automatically gets their permissions)
                          </p>
                        ) : (
                          <p className="text-muted-foreground">
                            This role does not inherit from any other role.
                          </p>
                        )}
                        {inheritanceTree.inheritance_tree[selectedRole].inherited_by?.length > 0 && (
                          <p className="text-muted-foreground">
                            <span className="text-primary font-medium">Inherited by:</span>{' '}
                            {inheritanceTree.inheritance_tree[selectedRole].inherited_by.map(r => 
                              inheritanceTree.inheritance_tree[r]?.label || r
                            ).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Permissions Tab */}
        <TabsContent value="permissions" className="space-y-4">
      
      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <Alert className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-300">
            You have unsaved changes. Click "Save Changes" to apply them.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Permissions Grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Section Access</CardTitle>
              <CardDescription>
                Check the sections and subtabs this role can access
              </CardDescription>
            </div>
            {selectedRole !== 'owner' && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiscard}
                  disabled={!hasChanges || saving}
                >
                  <X className="w-4 h-4 mr-1" />
                  Discard
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog(true)}
                  disabled={saving}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Accordion 
            type="multiple" 
            value={expandedSections}
            onValueChange={setExpandedSections}
            className="space-y-2"
          >
            {Object.entries(allData.sections).map(([sectionId, section]) => {
              const { enabled, total } = getEnabledCount(sectionId);
              const sectionEnabled = isSectionEnabled(sectionId);
              const isOwner = selectedRole === 'owner';
              const hasSubtabs = section.subtabs.length > 0;
              
              return (
                <AccordionItem 
                  key={sectionId} 
                  value={sectionId}
                  className={`border rounded-lg overflow-hidden ${
                    sectionEnabled 
                      ? 'border-primary/30 bg-primary/5 dark:bg-primary/10' 
                      : 'border-border bg-card'
                  }`}
                >
                  <div className="flex items-center px-4 py-3">
                    {/* Section Checkbox */}
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        id={`section-${sectionId}`}
                        checked={sectionEnabled}
                        onCheckedChange={() => !isOwner && toggleSection(sectionId)}
                        disabled={isOwner}
                        className="data-[state=checked]:bg-primary"
                      />
                      <label 
                        htmlFor={`section-${sectionId}`}
                        className={`font-medium cursor-pointer ${
                          sectionEnabled ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {section.label}
                      </label>
                      {hasSubtabs && (
                        <Badge variant="outline" className="text-xs">
                          {enabled}/{total} subtabs
                        </Badge>
                      )}
                    </div>
                    
                    {/* Expand Button */}
                    {hasSubtabs && (
                      <AccordionTrigger className="py-0 hover:no-underline">
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </AccordionTrigger>
                    )}
                  </div>
                  
                  {hasSubtabs && (
                    <AccordionContent className="px-4 pb-4">
                      {/* Select All / Deselect All */}
                      {!isOwner && (
                        <div className="flex gap-2 mb-3 pb-3 border-b">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleAllSubtabs(sectionId, true)}
                            disabled={enabled === total}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Select All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleAllSubtabs(sectionId, false)}
                            disabled={enabled === 0}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Deselect All
                          </Button>
                        </div>
                      )}
                      
                      {/* Subtabs Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {section.subtabs.map(subtab => {
                          const subtabEnabled = (permissions.subtabs[sectionId] || []).includes(subtab.id);
                          
                          return (
                            <div
                              key={subtab.id}
                              className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                subtabEnabled 
                                  ? 'bg-green-50 dark:bg-green-900/20' 
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                            >
                              <Checkbox
                                id={`subtab-${sectionId}-${subtab.id}`}
                                checked={subtabEnabled}
                                onCheckedChange={() => !isOwner && toggleSubtab(sectionId, subtab.id)}
                                disabled={isOwner}
                                className="data-[state=checked]:bg-green-600"
                              />
                              <label
                                htmlFor={`subtab-${sectionId}-${subtab.id}`}
                                className={`text-sm cursor-pointer ${
                                  subtabEnabled ? 'text-foreground' : 'text-muted-foreground'
                                }`}
                              >
                                {subtab.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  )}
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
      
      {/* Role Summary Cards */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Roles Overview
          </CardTitle>
          <CardDescription>
            Quick view of what each role can access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {allData.roles.map(role => {
              const rolePerms = allData.permissions[role.id];
              const sectionCount = rolePerms?.permissions?.sections?.length || 0;
              const totalSections = Object.keys(allData.sections).length;
              
              return (
                <div
                  key={role.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedRole === role.id 
                      ? 'border-primary ring-2 ring-primary/20' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedRole(role.id)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={ROLE_COLORS[role.id]}>
                      {role.label}
                    </Badge>
                    {role.id === 'owner' && <Lock className="w-3 h-3 text-muted-foreground" />}
                    {rolePerms?.is_custom && (
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                        Custom
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{role.description}</p>
                  <div className="flex items-center gap-1 text-sm">
                    <Eye className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{sectionCount}/{totalSections} sections</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
        </TabsContent>
      </Tabs>
      
      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Permissions?</DialogTitle>
            <DialogDescription>
              This will reset {currentRoleInfo?.label} role permissions to the default settings.
              Any custom changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReset} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}
              Reset to Defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Template Apply Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Apply Template
            </DialogTitle>
            <DialogDescription>
              Apply the "{selectedTemplate?.name}" template to the {currentRoleInfo?.label} role.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 dark:bg-muted/10 rounded-lg">
                <p className="text-sm text-muted-foreground mb-3">
                  {selectedTemplate.description}
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{selectedTemplate.section_count} sections</Badge>
                  <Badge variant="outline">{selectedTemplate.subtab_count} tabs</Badge>
                </div>
              </div>
              
              <Alert className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-300 text-sm">
                  This will replace all current permissions for the {currentRoleInfo?.label} role.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyTemplate} disabled={applyingTemplate}>
              {applyingTemplate ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Copy className="w-4 h-4 mr-1" />
              )}
              Apply Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
